<template>
    <!-- Rows: the popover rendering — name, one-line description, "Use".
         One control per row. The read affordance that used to sit beside it was
         removed on review (Aymeric, 2026-08-29): the picker stays simple, and
         "Manage" is the way to look at a prompt in full. -->
    <ul class="flex flex-col gap-0.5 m-0 p-0 list-none">
        <li v-for="p in filteredRows" :key="p.id"
            class="flex items-center rounded-md
                   hover:bg-primary-50 dark:hover:bg-primary-950 focus-within:bg-primary-50">
            <button type="button" data-testid="saved-prompt-row"
                class="grid grid-cols-[1fr_auto] items-center gap-3 flex-1 min-w-0 text-left
                       rounded-md px-2.5 py-2 cursor-pointer outline-none focus-visible:bg-primary-50"
                @click="emit('use', toRaw(p))">
                <span class="min-w-0 block">
                    <span class="block text-sm font-semibold truncate">{{ p.name }}</span>
                    <span v-if="p.description" class="block text-xs text-surface-500 line-clamp-2">{{ p.description }}</span>
                </span>
                <span class="text-[11px] font-bold uppercase tracking-wide text-primary">{{ t('chat.savedPrompts.use') }}</span>
            </button>
        </li>
        <li v-if="filteredRows.length === 0" class="px-2.5 py-3 text-sm text-surface-500">
            {{ t('chat.savedPrompts.empty') }}
        </li>
    </ul>
</template>

<script setup lang="ts">
/**
 * SavedPromptsList (MYAC-1106) — the searchable rows of a project's saved
 * prompts, rendered inside SavedPromptsButton's popover. Emits `use(prompt)`;
 * the parent decides what "use" does (prefill via useSavedPromptPrefill).
 */
import { computed, toRaw } from 'vue'
import { useI18n } from 'vue-i18n'
import type { SavedPrompt } from '@/models/SavedPrompt'

const props = withDefaults(defineProps<{
    prompts: SavedPrompt[]
    /** Case-insensitive filter on name/description. */
    query?: string
}>(), { query: '' })

const emit = defineEmits<{
    (e: 'use', prompt: SavedPrompt): void
}>()

const { t } = useI18n()

const filteredRows = computed(() => {
    const q = props.query.trim().toLowerCase()
    if (!q) return props.prompts
    return props.prompts.filter((p) =>
        p.name.toLowerCase().includes(q) || (p.description ?? '').toLowerCase().includes(q))
})
</script>
